import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { ReactSVG } from "react-svg";
import Button from "./Button";
import SimpleIconButton from "./SimpleIconButton";
import "../style/typography.scss";
import "../style/card.scss";
import xSVG from "../icon/X.svg";
import { setSVGColor } from "../SVGHelper";
import "../style/modal.scss";

/**
 * A modal dialog component that occludes the whole page and blocks interaction until complete.
 *
 * Has onCancel instead of onClose because any other closing event should be handled via customButtonProps.
 *
 * Button Customization:
 * - Props objects passed to props.cancelButtonProps or in the array to props.customButtonProps can contain any valid props for the Button component.
 * - If a className prop is included, the default classes will be lost, make sure to use all necessary classes from buttons.scss.
 * - If a className prop is NOT included, the button will be styled according to the brand guide for modals.
 * - If a style prop is included, it will be merged with the default modal button style, taking priority.
 * - All other props will override.
 *
 * @see Button - for more details on how to customize the button properties.
 * @see buttons.scss - for more details on the available button classes.
 *
 * @param {object} props - React props object
 * @param {boolean} props.isOpen - Determines whether the modal is open or not. Default is `false`.
 * @param {React.ReactNode} props.children - The content of the modal.
 * @param {object} props.cancelButtonProps - Props for the cancel button.
 * @param {array<object>} props.customButtonProps - An array of props objects for custom buttons to be added to the modal. Each props object contains the props for a single button to be added.
 * @param {string} props.title - The title of the modal.
 * @param {string} props.icon - SVG path data for the icon. Default is `null`.
 * @param {string} props.iconColor - CSS color value for the icon. Default is `'var(--colors-primary-slate-500)'`.
 * @param {React.ReactNode} props.extraHeaderContent - Additional content to be added to the top bar of the modal. Default is `null`.
 * @param {boolean} props.compact - Determines whether the modal should be compact or not, right now this just determines if the title should inline with the svg and x button or below. Default is `false`.
 * @param {boolean} props.showXButton - Determines whether the modal should have an X button at the top. Default is `false`.
 * @param {function} props.onCancel - Callback function for the cancel button's click event. It's separate from `cancelButtonProps.onClick` to allow additional cancel logic.
 * @param {function} props.onClickOutside - This defaults to a function that just calls onCancel, so if you pass this make sure to cancel if thats what you want.
 * @param {object} props.style - Style object to be applied to the modal, this is applied to the outermost div of the modal(the card) not the screen filling background.
 * @param {object} props.styleExcludingTop - Applied to all of the modal except the very top bar, containing the x button and icon (and title) in compact mode.
 *
 * @example
 *    <Modal
 *      title='Custom Buttons'
 *      isOpen={customButtonsModalOpen}
 *      onCancel={() => {
 *        setCustomButtonsModalOpen(false);
 *      }}
 *      customButtonProps={[
 *        {
 *          children: 'Kill',
 *          className: 'btn-primary-destructive btn-lg',
 *          style: { boxShadow: '0px 8px 13px rgba(0, 0, 0, .7)' },
 *          onClick: () => setAlive(false)
 *        },
 *        {
 *          children: 'Accept',
 *          onClick: () => setCustomButtonsModalOpen(false)
 *        },
 *      ]}
 *    >
 *      content
 *    </Modal>
 */
function Modal({
    isOpen = false,
    children,
    cancelButtonProps = {},
    customButtonProps = [],
    title = "",
    icon = null,
    iconColor = "var(--colors-primary-slate-500)",
    extraHeaderContent = null,
    compact = false,
    showXButton = false,
    showCancel = true,
    onCancel,
    onClickOutside = (e) => {
        onCancel?.();
    },
    style = {},
    styleExcludingTop = {},
}) {
    useEffect(() => {
        /** @param {KeyboardEvent} event */
        function keydownListener(event) {
            if (event.key === "Escape") {
                onCancel?.();
            }
        }

        window.addEventListener("keydown", keydownListener);

        return () => {
            window.removeEventListener("keydown", keydownListener);
        };
    }, [onCancel]);

    const modalRef = React.useRef();
    if (!isOpen) return null;

    const onBackdropClicked = (e) => {
        //check not actually inside the modal
        if (!modalRef.current.contains(e.target)) {
            onClickOutside?.(e);
        }
    };

    const titleComponent = (
        <p
            className="description-4-semibold"
            style={{
                color: "var(--colors-primary-slate-800)",
                marginBottom: "6px",
            }}
        >
            {title}
        </p>
    );

    const buttonStyle = {
        minWidth: "160px",
        textAlign: "center",
    };

    return ReactDOM.createPortal(
        <div className="modal-backdrop" onClick={onBackdropClicked}>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    padding: "20px",
                    overflow: "hidden",
                    maxHeight: "90%",
                    maxWidth: "90%",
                    ...style,
                }}
                ref={modalRef}
                className="modal-container card card--sm"
            >
                {/* Adding scrollbar to an inner element ensures the border radius is kept intact on the outer element when a scrollbar appears */}
                <div style={{ overflow: "auto" }}>
                    {/* top bar */}
                    <div style={{ display: "flex", width: "100%", gap: "8px" }}>
                        {/* top bar icon */}
                        {icon && <ReactSVG src={icon} beforeInjection={setSVGColor(iconColor)} />}

                        {/* extras */}
                        {extraHeaderContent}

                        {/* title here in compact mode */}
                        {compact && titleComponent}

                        {/* x button */}
                        {showXButton && (
                            <SimpleIconButton src={xSVG} onClick={onCancel} style={{ marginLeft: "auto" }} />
                        )}
                    </div>

                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            height: "100%",
                            ...styleExcludingTop,
                        }}
                    >
                        {/* title here when not compact, div wrapped for consistent block layout */}
                        {!compact && <div style={{ width: "100%" }}>{titleComponent}</div>}

                        {/* content */}
                        {children}

                        {/* button area */}
                        {showCancel || customButtonProps?.length ? (
                            <div
                                style={{
                                    marginTop: "auto",
                                    paddingTop: "20px",
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    width: "100%",
                                    gap: "10px",
                                    alignItems: "center",
                                }}
                            >
                                {/* cancel button, children set in prop so it can be overridden by cancelButtonProps */}
                                {showCancel && (
                                    <Button
                                        children={"Cancel"}
                                        onClick={onCancel}
                                        className={"btn-secondary-gray btn-sm"}
                                        style={buttonStyle}
                                        {...cancelButtonProps}
                                    />
                                )}

                                {/* custom buttons, need to apply style prop separately to merge with default modal button style */}
                                {customButtonProps.map(({ style, ...restProps }, index) => (
                                    <Button
                                        className={"btn-primary btn-sm"}
                                        style={{
                                            ...buttonStyle,
                                            ...style,
                                        }}
                                        {...restProps}
                                        key={index}
                                    />
                                ))}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}

export default Modal;
